import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type {
  ReviewState,
  CreateReviewWithMediaDto,
  UpdateReviewWithMediaDto,
  MyReviewResponse,
  UpdateReviewStatusDto,
  ReviewQueryDto,
  ReviewsResponse,
  Review,
  ProductRating,
  ProductReviewPercentage,
  PaginationMeta,
} from "./types";
import { getCookie } from "../../utility/getCookie";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_ECOMMERCE_PERFIX) {
  console.warn("Warning: REACT_APP_ECOMMERCE_PERFIX is not defined.");
}

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        token = getCookie("access_token");
      } catch (err) {
        // Handle refresh failure (e.g., redirect to login)
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
const initialState: ReviewState = {
  reviews: [],
  currentReview: null,
  myReview: null,
  canEditMyReview: true,
  editDaysLeft: 5,
  productReviews: [],
  productRating: null,
  productReviewPercentage: null,
  pagination: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
};

/**
 * Fetch customer's own review for a specific product and check 5-day edit window
 */
export const fetchMyReviewForProduct = createAsyncThunk(
  "reviews/fetchMyReviewForProduct",
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<MyReviewResponse>(
        `/reviews/my-review/${productId}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch your review",
      );
    }
  },
);

/**
 * Update an existing review within the 5-day edit window
 */
export const updateCustomerReview = createAsyncThunk(
  "reviews/updateCustomerReview",
  async (
    {
      reviewId,
      data,
    }: { reviewId: string; data: UpdateReviewWithMediaDto },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put<Review>(`/reviews/${reviewId}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update review",
      );
    }
  },
);

/**
 * Create a new review with media files (images and videos)
 */
export const createReviewWithMedia = createAsyncThunk(
  "reviews/createWithMedia",
  async (data: CreateReviewWithMediaDto, { rejectWithValue }) => {
    try {
      const response = await api.post<Review>(`/reviews`, data);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create review",
      );
    }
  },
);

/**
 * Create a new review with media files (images and videos)
 */
export const createFoodReview = createAsyncThunk(
  "reviews/createFoodReview",
  async (data: CreateReviewWithMediaDto, { rejectWithValue }) => {
    try {
      const response = await api.post<Review>(`/reviews/foodreview`, data);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create food review",
      );
    }
  },
);

/**
 * Get reviews by product ID (published only)
 */
export const getReviewsByProductId = createAsyncThunk(
  "reviews/getByProductId",
  async (
    {
      productId,
      page = 1,
      limit = 10,
    }: { productId: string; page?: number; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const url = `/reviews/product/${productId}?${params.toString()}`;

      const response = await api.get<ReviewsResponse>(url);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch product reviews",
      );
    }
  },
);

/**
 * Get overall rating for a product
 */
export const getProductOverallRating = createAsyncThunk(
  "reviews/getProductRating",
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ProductRating>(
        `/reviews/product/${productId}/rating`,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch product rating",
      );
    }
  },
);

/**
 * Get product review percentage distribution
 */
export const getProductReviewPercentage = createAsyncThunk(
  "reviews/getProductPercentage",
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ProductReviewPercentage>(
        `/reviews/product/${productId}/percentage`,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch review percentage",
      );
    }
  },
);

const reviewSlice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentReview: (state) => {
      state.currentReview = null;
    },
    clearProductReviews: (state) => {
      state.productReviews = [];
      state.pagination = null;
    },
    clearProductRating: (state) => {
      state.productRating = null;
    },
    clearProductReviewPercentage: (state) => {
      state.productReviewPercentage = null;
    },
    clearMyReview: (state) => {
      state.myReview = null;
      state.canEditMyReview = true;
      state.editDaysLeft = 5;
    },
    resetReviewState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch My Review For Product
    builder.addCase(fetchMyReviewForProduct.fulfilled, (state, action) => {
      state.myReview = action.payload.review;
      state.canEditMyReview = action.payload.canEdit;
      state.editDaysLeft = action.payload.daysLeft;
    });

    // Update Customer Review
    builder.addCase(updateCustomerReview.pending, (state) => {
      state.isUpdating = true;
      state.error = null;
    });
    builder.addCase(
      updateCustomerReview.fulfilled,
      (state, action: PayloadAction<Review>) => {
        state.isUpdating = false;
        state.myReview = action.payload;
        state.currentReview = action.payload;
        // Update in productReviews if present
        const idx = state.productReviews.findIndex(
          (r) => String(r._id) === String(action.payload._id),
        );
        if (idx !== -1) {
          state.productReviews[idx] = action.payload;
        }
      },
    );
    builder.addCase(updateCustomerReview.rejected, (state, action) => {
      state.isUpdating = false;
      state.error = action.payload as string;
    });

    // Create Review With Media
    builder.addCase(createReviewWithMedia.pending, (state) => {
      state.isCreating = true;
      state.error = null;
    });
    builder.addCase(
      createReviewWithMedia.fulfilled,
      (state, action: PayloadAction<Review>) => {
        state.isCreating = false;
        state.currentReview = action.payload;
        state.reviews.unshift(action.payload);
      },
    );
    builder.addCase(createReviewWithMedia.rejected, (state, action) => {
      state.isCreating = false;
      state.error = action.payload as string;
    });

    builder.addCase(createFoodReview.pending, (state) => {
      state.isCreating = true;
      state.error = null;
    });
    builder.addCase(
      createFoodReview.fulfilled,
      (state, action: PayloadAction<Review>) => {
        state.isCreating = false;
        state.currentReview = action.payload;
        state.reviews.unshift(action.payload);
      },
    );
    builder.addCase(createFoodReview.rejected, (state, action) => {
      state.isCreating = false;
      state.error = action.payload as string;
    });

    // Get Reviews By Product ID
    builder.addCase(getReviewsByProductId.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getReviewsByProductId.fulfilled,
      (state, action: PayloadAction<ReviewsResponse>) => {
        state.isLoading = false;
        state.productReviews = action.payload.reviews;
        state.pagination = action.payload.pagination;
      },
    );
    builder.addCase(getReviewsByProductId.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get Product Overall Rating
    builder.addCase(getProductOverallRating.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getProductOverallRating.fulfilled,
      (state, action: PayloadAction<ProductRating>) => {
        state.isLoading = false;
        state.productRating = action.payload;
      },
    );
    builder.addCase(getProductOverallRating.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get Product Review Percentage
    builder.addCase(getProductReviewPercentage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getProductReviewPercentage.fulfilled,
      (state, action: PayloadAction<ProductReviewPercentage>) => {
        state.isLoading = false;
        state.productReviewPercentage = action.payload;
      },
    );
    builder.addCase(getProductReviewPercentage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  clearError,
  clearCurrentReview,
  clearProductReviews,
  clearProductRating,
  clearProductReviewPercentage,
  clearMyReview,
  resetReviewState,
} = reviewSlice.actions;

export default reviewSlice.reducer;
